import { useCallback, useEffect, useRef } from 'react';

/** Frist, in der ein zweiter Klick das Tappen zum Hinweis-Ping macht. */
export const TAP_SEND_DELAY_MS = 250;

/**
 * Einzelklick vs. Doppelklick auf einer Brettkarte.
 *
 * Ein Klick dreht die Karte sofort (`onTapSend(id, { optimisticOnly: true, tapped })`, `tapped` ist
 * der Zielzustand) und sendet erst nach `TAP_SEND_DELAY_MS` (`onTapSend(id, { send: true })`).
 * Folgt in der Frist ein Doppelklick, fällt das Senden weg: `onTapSend(id, { revert: true })`
 * dreht die Karte zurück, `onPing(id)` gibt den Hinweis. Ein Doppelklick nach der Frist pingt nur
 * noch — das Tappen ist dann schon unterwegs.
 *
 * Der Browser meldet einen Doppelklick als click, click, dblclick; der zweite Klick in der Frist
 * wird deshalb ignoriert, sonst drehte er die Karte gleich wieder zurück. Wer die Callbacks
 * umsetzt (Vormerkung, Socket), entscheidet die Seite. Beim Unmount gehen offene Tapp-Vorgänge
 * sofort raus, damit eine bereits gedrehte Karte nicht nur lokal gedreht bleibt.
 */
export function useDeferredTap({ onTapSend, onPing }) {
  const timersRef = useRef(new Map());
  const callbacksRef = useRef({ onTapSend, onPing });
  callbacksRef.current = { onTapSend, onPing };

  useEffect(() => () => {
    const timers = timersRef.current;
    timers.forEach((timer, instanceId) => {
      clearTimeout(timer);
      callbacksRef.current.onTapSend(instanceId, { send: true });
    });
    timers.clear();
  }, []);

  const onClick = useCallback((instanceId, currentTapped) => {
    const timers = timersRef.current;
    if (timers.has(instanceId)) return;
    callbacksRef.current.onTapSend(instanceId, { optimisticOnly: true, tapped: !currentTapped });
    timers.set(instanceId, setTimeout(() => {
      timers.delete(instanceId);
      callbacksRef.current.onTapSend(instanceId, { send: true });
    }, TAP_SEND_DELAY_MS));
  }, []);

  const onDoubleClick = useCallback((instanceId) => {
    const timers = timersRef.current;
    if (timers.has(instanceId)) {
      clearTimeout(timers.get(instanceId));
      timers.delete(instanceId);
      callbacksRef.current.onTapSend(instanceId, { revert: true });
    }
    callbacksRef.current.onPing(instanceId);
  }, []);

  return { onClick, onDoubleClick };
}
